import React, { useState, useEffect, useCallback } from 'react';
import { Tag } from 'primereact/tag';
import axios from 'axios';
import { url } from '../../../../../env';
import { AppButton, AppIconButton, AppNumberField } from '../../../../../Components/FormKit';
import { AppTableCard } from '../../../../../Components/TableKit';

/**
 * Dictamen de Capacidad de Pago calculado en backend (Prioridad #2).
 *
 * Sustituye al indicador que este panel calculaba en el cliente
 * (`saldo_promedio / renta_pei`), que medía LIQUIDEZ, no capacidad de pago:
 * no descontaba traspasos entre cuentas propias, no excluía préstamos y no
 * consideraba las obligaciones ya reportadas en Buró.
 *
 * Aquí no se calcula nada: el backend es la única fuente de los números para
 * que el dictamen, ARIA y esta vista nunca discrepen. Los umbrales provienen
 * de `acp/metodologia/03-capacidad-pago.md`.
 */

// ─── Paleta por semáforo ──────────
const SEMAFORO = {
    verde: { color: '#15803d', bg: '#dcfce7', borde: '#16a34a', icono: 'pi-check-circle' },
    amarillo: { color: '#a16207', bg: '#fef3c7', borde: '#d97706', icono: 'pi-exclamation-triangle' },
    rojo: { color: '#b91c1c', bg: '#fee2e2', borde: '#dc2626', icono: 'pi-times-circle' },
    sin_datos: { color: '#64748b', bg: '#f1f5f9', borde: '#94a3b8', icono: 'pi-question-circle' },
};

const NIVEL_ALERTA = {
    danger: { color: '#b91c1c', bg: '#fef2f2', icono: 'pi-times-circle', label: 'Crítico' },
    warning: { color: '#a16207', bg: '#fffbeb', icono: 'pi-exclamation-triangle', label: 'Atención' },
    info: { color: '#1d4ed8', bg: '#eff6ff', icono: 'pi-info-circle', label: 'Nota' },
};

const estilo = (semaforo) => SEMAFORO[semaforo] || SEMAFORO.sin_datos;

// ─── Formato ──────────
const money = (v) =>
    v === null || v === undefined || isNaN(v)
        ? '—'
        : new Intl.NumberFormat('es-MX', {
            style: 'currency', currency: 'MXN', maximumFractionDigits: 0,
        }).format(v);

const pct = (v) =>
    v === null || v === undefined || isNaN(v) ? '—' : `${Number(v).toFixed(2)}%`;

const veces = (v) =>
    v === null || v === undefined || isNaN(v) || !isFinite(v) ? '—' : `${Number(v).toFixed(2)}x`;

// ─── Tarjeta de métrica con su umbral ──────────
const MetricaCard = ({ titulo, valor, semaforo, explicacion, umbral }) => {
    const e = estilo(semaforo?.nivel);
    return (
        <div
            className="bg-white border-1 surface-border border-round-lg p-3"
            style={{ flex: '1 1 200px', borderTop: `3px solid ${e.borde}` }}
        >
            <div className="flex justify-content-between align-items-start mb-2">
                <span
                    className="text-xs font-bold text-700"
                    style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
                >
                    {titulo}
                </span>
                <i className={`pi ${e.icono}`} style={{ color: e.borde, fontSize: '0.95rem' }} />
            </div>
            <div className="text-2xl font-bold mb-1" style={{ color: e.color }}>
                {valor}
            </div>
            <div className="text-xs text-600" style={{ lineHeight: 1.4 }}>
                {explicacion}
            </div>
            {umbral && (
                <div className="text-xs text-500 mt-2 pt-2 border-top-1 surface-border">
                    Umbral: {umbral}
                </div>
            )}
        </div>
    );
};

// ─── Fila del desglose del cálculo ──────────
const FilaCalculo = ({ etiqueta, valor, signo, nota, total, destacado }) => (
    <tr style={total ? { borderTop: '2px solid var(--surface-border)' } : undefined}>
        <td className="p-2" style={{ width: '2rem', color: '#94a3b8' }}>{signo}</td>
        <td className={`p-2 ${total || destacado ? 'font-bold text-900' : 'text-700'}`}>
            {etiqueta}
            {nota && <div className="text-xs text-500 font-normal mt-1">{nota}</div>}
        </td>
        <td
            className={`p-2 tk-td-right ${total || destacado ? 'font-bold' : 'font-medium'}`}
            style={destacado ? { color: '#7B1FA2' } : undefined}
        >
            {money(valor)}
        </td>
    </tr>
);

// ─── Panel principal ──────────
const CapacidadPagoDictamen = ({ persona, toast }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [verDetalle, setVerDetalle] = useState(false);

    // Simulación de mitigantes: recalcula con otra renta sin guardar nada
    const [simulando, setSimulando] = useState(false);
    const [rentaSimulada, setRentaSimulada] = useState(null);

    const fetch = useCallback(async (mensualidad = null) => {
        if (!persona?.id) return;
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const params = {};
            if (mensualidad !== null && mensualidad > 0) params.mensualidad = mensualidad;

            const res = await axios.get(`${url}/document-analysis/capacidad-pago/${persona.id}`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });
            setData(res.data?.capacidad_pago ?? null);
        } catch (err) {
            const msg = err.response?.data?.message || 'No se pudo calcular la capacidad de pago.';
            setError(msg);
            toast?.current?.show({ severity: 'error', summary: 'Error', detail: msg, life: 4000 });
        } finally {
            setLoading(false);
        }
    }, [persona?.id, toast]);

    useEffect(() => { fetch(); }, [fetch]);

    // ── Estados de carga y error ──
    if (loading && !data) {
        return (
            <div className="flex flex-column gap-3 p-3">
                <style>{'@keyframes cpSkel{0%{background-position:200% 0}100%{background-position:-200% 0}}'}</style>
                {[3, 8, 12].map((h, i) => (
                    <div key={i} style={{
                        height: `${h}rem`,
                        background: 'linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)',
                        backgroundSize: '200% 100%', borderRadius: '6px',
                        animation: 'cpSkel 1.4s infinite',
                    }} />
                ))}
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="flex flex-column align-items-center justify-content-center gap-3 p-4">
                <i className="pi pi-exclamation-circle text-3xl text-400" />
                <span className="text-sm text-600">{error}</span>
                <AppButton label="Reintentar" icon="pi pi-refresh" variant="secondary" size="sm" onClick={() => fetch()} />
            </div>
        );
    }

    if (!data) return null;

    const { veredicto, score, metricas: m, semaforos: s, desglose: d, alertas = [], mitigantes_sugeridos: mit = [], umbrales: u, datos_suficientes } = data;
    const e = estilo(veredicto?.semaforo);
    const irm = d?.ingreso?.solicitante;
    const buro = d?.buro;

    return (
        <div className="flex flex-column gap-3">
            {/* ── Encabezado ── */}
            <div className="flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-calculator text-lg" style={{ color: '#7B1FA2' }} />
                    <span
                        className="text-sm font-bold"
                        style={{ color: '#7B1FA2', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                    >
                        Capacidad de Pago
                    </span>
                    <Tag
                        value="Calculado en backend"
                        rounded
                        style={{ fontSize: '0.65rem', background: '#F3E8FF', color: '#7B1FA2' }}
                    />
                </div>
                <AppIconButton icon="pi pi-refresh" round loading={loading} title="Recalcular" onClick={() => fetch()} />
            </div>

            {/* ── Veredicto ── */}
            <div
                className="border-round-lg p-3 flex align-items-start gap-3"
                style={{ background: e.bg, border: `1px solid ${e.borde}` }}
            >
                <i className={`pi ${e.icono}`} style={{ color: e.color, fontSize: '1.6rem', marginTop: '0.15rem' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="flex align-items-center gap-2 flex-wrap">
                        <span className="text-lg font-bold" style={{ color: e.color }}>
                            {veredicto?.label || 'Sin dictamen'}
                        </span>
                        {score !== null && score !== undefined && (
                            <span
                                className="text-xs font-bold px-2 py-1 border-round"
                                style={{ background: 'rgba(255,255,255,0.75)', color: e.color }}
                            >
                                Score {score}/100
                            </span>
                        )}
                    </div>
                    <div className="text-sm mt-1" style={{ color: e.color, lineHeight: 1.5 }}>
                        {veredicto?.motivo}
                    </div>
                </div>
            </div>

            {!datos_suficientes && (
                <div className="text-xs text-600 p-2 border-round" style={{ background: '#f8fafc' }}>
                    Faltan insumos para dictaminar. Se muestra lo que sí se pudo calcular.
                </div>
            )}

            {/* ── Las tres métricas de la metodología ── */}
            <div className="flex gap-3 flex-wrap">
                <MetricaCard
                    titulo="Aforo"
                    valor={pct(m?.aforo_pct)}
                    semaforo={s?.aforo}
                    explicacion="Renta mensual sobre el ingreso recurrente."
                    umbral={u ? `≤${u.aforo.sano}% sano · >${u.aforo.vigilar}% rechazo` : null}
                />
                <MetricaCard
                    titulo="DSCR"
                    valor={veces(m?.dscr)}
                    semaforo={s?.dscr}
                    explicacion="Flujo disponible entre compromisos totales."
                    umbral={u ? `≥${u.dscr.sano}x sano · <${u.dscr.vigilar}x rechazo` : null}
                />
                <MetricaCard
                    titulo="Endeudamiento total"
                    valor={pct(m?.endeudamiento_total_pct)}
                    semaforo={s?.endeudamiento}
                    explicacion="Buró más la renta, sobre el ingreso."
                    umbral={u ? `≤${u.endeudamiento.sano}% sano · >${u.endeudamiento.vigilar}% rechazo` : null}
                />
            </div>

            {/* ── Desglose del cálculo ── */}
            <AppTableCard animated={false}>
                <table className="tk-table tk-table--compact">
                    <thead>
                        <tr>
                            <th colSpan={2}>Cómo se llegó al resultado</th>
                            <th className="tk-th-right">Mensual</th>
                        </tr>
                    </thead>
                    <tbody>
                        <FilaCalculo
                            etiqueta="Ingreso recurrente (IRM)"
                            valor={m?.irm}
                            nota={irm ? `${irm.num_periodos} periodo(s) · criterio: ${irm.criterio}` : null}
                        />
                        <FilaCalculo
                            signo="−"
                            etiqueta="Gasto operativo observado"
                            valor={m?.gasto_operativo_estimado}
                            nota="Egresos del estado de cuenta, sin traspasos propios ni pagos ya contados en Buró."
                        />
                        <FilaCalculo etiqueta="Flujo disponible" valor={m?.flujo_disponible} total />
                        <tr><td colSpan={3} style={{ height: '0.5rem' }} /></tr>
                        <FilaCalculo
                            etiqueta="Pago mensual en Buró"
                            valor={m?.pago_mensual_buro}
                            nota={buro?.disponible
                                ? `${buro.num_cuentas_vigentes} de ${buro.num_cuentas_totales} obligaciones vigentes`
                                : 'Sin reporte de Buró'}
                        />
                        {m?.pago_mensual_buro_aval > 0 && (
                            <FilaCalculo signo="+" etiqueta="Buró del aval (consolidado)" valor={m.pago_mensual_buro_aval} />
                        )}
                        <FilaCalculo
                            signo="+"
                            etiqueta="Renta Peicar propuesta"
                            valor={m?.mensualidad_peicar}
                            nota={d?.mensualidad?.iva_indeterminado
                                ? 'IVA no confirmado: el aforo real podría ser ~16% mayor.'
                                : d?.mensualidad?.origen}
                        />
                        <FilaCalculo etiqueta="Compromisos totales" valor={m?.compromisos_totales} total />
                        <FilaCalculo
                            etiqueta="Flujo remanente"
                            valor={m?.flujo_remanente}
                            destacado
                            nota={m?.flujo_remanente < 0 ? 'Negativo: no alcanza para cubrir los compromisos.' : null}
                        />
                    </tbody>
                </table>
            </AppTableCard>

            {/* ── Alertas ── */}
            {alertas.length > 0 && (
                <div className="flex flex-column gap-2">
                    <div className="flex align-items-center gap-2 pb-2 border-bottom-1 surface-border">
                        <i className="pi pi-flag text-sm" style={{ color: '#7B1FA2' }} />
                        <span
                            className="text-sm font-bold text-700"
                            style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
                        >
                            Hallazgos ({alertas.length})
                        </span>
                    </div>
                    {alertas.map((a, i) => {
                        const n = NIVEL_ALERTA[a.nivel] || NIVEL_ALERTA.info;
                        return (
                            <div
                                key={`${a.codigo}-${i}`}
                                className="flex align-items-start gap-2 p-2 border-round"
                                style={{ background: n.bg, borderLeft: `3px solid ${n.color}` }}
                            >
                                <i className={`pi ${n.icono}`} style={{ color: n.color, fontSize: '0.85rem', marginTop: '0.15rem' }} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <span className="text-sm text-700" style={{ lineHeight: 1.5 }}>{a.mensaje}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Mitigantes y simulación ── */}
            {mit.length > 0 && (
                <div className="bg-white border-1 surface-border border-round-lg p-3">
                    <div className="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                        <span className="text-sm font-bold text-900">Cómo se podría salvar la operación</span>
                        <AppButton
                            label={simulando ? 'Cerrar simulación' : 'Simular otra renta'}
                            icon={simulando ? 'pi pi-times' : 'pi pi-sliders-h'}
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                                const max = mit.find((x) => x.tipo === 'reducir_mensualidad')?.mensualidad_maxima;
                                setRentaSimulada(max ?? m?.mensualidad_peicar ?? null);
                                setSimulando((v) => !v);
                            }}
                        />
                    </div>

                    <ul className="m-0 pl-3 text-sm text-700" style={{ lineHeight: 1.7 }}>
                        {mit.map((x, i) => <li key={`${x.tipo}-${i}`}>{x.mensaje}</li>)}
                    </ul>

                    {simulando && (
                        <div className="flex align-items-end gap-2 mt-3 pt-3 border-top-1 surface-border flex-wrap">
                            <div style={{ minWidth: '200px' }}>
                                <AppNumberField
                                    label="Renta a simular"
                                    value={rentaSimulada}
                                    onValueChange={(ev) => setRentaSimulada(ev.value)}
                                    mode="currency"
                                    currency="MXN"
                                    currencyDisplay="narrowSymbol"
                                    useGrouping
                                />
                            </div>
                            <AppButton
                                label="Recalcular"
                                icon="pi pi-play"
                                variant="primary"
                                size="sm"
                                loading={loading}
                                onClick={() => fetch(rentaSimulada)}
                            />
                            <span className="text-xs text-500 pb-2">
                                La simulación no guarda nada; solo recalcula.
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* ── Detalle auditable de Buró ── */}
            {buro?.disponible && buro.cuentas?.length > 0 && (
                <div>
                    <button
                        className="p-link flex align-items-center gap-2 text-sm font-medium"
                        style={{ color: '#7B1FA2', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 0' }}
                        onClick={() => setVerDetalle((v) => !v)}
                    >
                        <i className={`pi ${verDetalle ? 'pi-chevron-down' : 'pi-chevron-right'}`} style={{ fontSize: '0.75rem' }} />
                        Detalle de obligaciones en Buró ({buro.cuentas.length})
                    </button>

                    {verDetalle && (
                        <AppTableCard animated={false}>
                            <table className="tk-table tk-table--compact tk-table--striped">
                                <thead>
                                    <tr>
                                        <th>Otorgante</th>
                                        <th className="tk-th-right">Saldo</th>
                                        <th className="tk-th-right">Vencido</th>
                                        <th className="tk-th-right">Pago mensual</th>
                                        <th>Cómo se estimó</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {buro.cuentas.map((c, i) => (
                                        <tr key={i} style={c.vigente ? undefined : { opacity: 0.55 }}>
                                            <td className="p-2 text-900">
                                                {c.otorgante || '—'}
                                                {!c.vigente && (
                                                    <div className="text-xs text-500 mt-1">{c.motivo_exclusion}</div>
                                                )}
                                            </td>
                                            <td className="p-2 tk-td-right">{money(c.saldo)}</td>
                                            <td className="p-2 tk-td-right" style={c.saldo_vencido > 0 ? { color: '#dc2626', fontWeight: 600 } : undefined}>
                                                {c.saldo_vencido > 0 ? money(c.saldo_vencido) : '—'}
                                            </td>
                                            <td className="p-2 tk-td-right font-medium">{money(c.pago_mensual_estimado)}</td>
                                            <td className="p-2 text-xs text-600">{c.metodo_estimacion || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </AppTableCard>
                    )}

                    {/* La carga incompleta se declara: un 0 por dato ilegible no es un 0 real */}
                    {buro.carga_completa === false && (
                        <div className="text-xs p-2 mt-2 border-round" style={{ background: '#fffbeb', color: '#a16207' }}>
                            <i className="pi pi-exclamation-triangle mr-1" style={{ fontSize: '0.75rem' }} />
                            {buro.obligaciones_sin_pago_estimable} obligación(es) sin pago reportado
                            {buro.saldo_sin_pago_estimable > 0 && ` (${money(buro.saldo_sin_pago_estimable)} de saldo)`}.
                            El endeudamiento real es mayor al calculado.
                        </div>
                    )}
                </div>
            )}

            <div className="text-xs text-500">
                Umbrales según metodología ACP · Calculado {data.calculado_at ? new Date(data.calculado_at).toLocaleString('es-MX') : '—'}
            </div>
        </div>
    );
};

export default CapacidadPagoDictamen;
